import Layout from "../components/site/Layout";
import { PageHeader } from "../components/site/Primitives";
import NextStep from "../components/site/NextStep";
import { ArrowUpRight } from "@phosphor-icons/react";
import { FIRM } from "../lib/firm";
import { trackEvent } from "../lib/analytics";

const CASES = [
  {
    tag: "Case 01",
    headline: "Married couple · Ages 64 and 62 · Texas · Recently retired.",
    facts: [
      { k: "IRA balance", v: "$2,800,000" },
      { k: "Projected RMDs at 73", v: "$140,000+ / yr" },
      { k: "IRMAA exposure", v: "$8,000 – $12,000 / yr" },
      { k: "Existing Roth plan", v: "None" },
    ],
    strategies: [
      "Multi-year Roth conversion sequencing during pre-RMD years",
      "IRMAA bracket management",
      "Beneficiary restructuring under the SECURE Act",
    ],
    outcomeLabel: "Projected lifetime tax savings",
    outcome: "$1,300,000",
  },
  {
    tag: "Case 02",
    headline:
      "Single retired professional · Age 68 · High W-2 income through retirement.",
    facts: [
      { k: "IRA / 401(k) balance", v: "$5,600,000" },
      { k: "Projected RMDs at 73", v: "$280,000+ / yr" },
      { k: "Heir exposure", v: "10-yr forced distribution" },
      { k: "Conversion structure", v: "IRA-LLC candidate" },
    ],
    strategies: [
      "IRA-LLC structure with valuation discount at conversion",
      "Multi-year Roth conversion plan",
      "Trust-based beneficiary restructuring",
    ],
    outcomeLabel: "Projected lifetime & inheritance tax savings",
    outcome: "$4,400,000",
  },
];

export default function ResultsPage() {
  return (
    <Layout pageName="results">
      <PageHeader
        eyebrow="Client Outcomes"
        title="What the opportunity looks like"
        italicTail="in practice."
        intro="The following are illustrative examples based on real client engagements. Identifying details have been removed."
      />

      <section className="pb-24 lg:pb-32 bg-cream" data-testid="results-cases">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 space-y-10">
          {CASES.map((c, i) => (
            <article
              key={c.tag}
              data-testid={`results-case-${i + 1}`}
              className="grid lg:grid-cols-12 border border-sage/40"
            >
              <div className="lg:col-span-7 bg-cream p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-sage/40">
                <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold">
                  {c.tag}
                </div>
                <h2 className="mt-4 font-serif text-deep-teal text-2xl lg:text-4xl leading-snug">
                  {c.headline}
                </h2>

                <dl className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-6">
                  {c.facts.map((f) => (
                    <div key={f.k}>
                      <dt className="text-[10px] tracking-overline uppercase font-semibold text-charcoal-teal/60">
                        {f.k}
                      </dt>
                      <dd className="mt-1 font-serif text-deep-teal text-2xl">
                        {f.v}
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-10 pt-8 border-t border-sage-soft">
                  <div className="text-[10px] tracking-overline uppercase font-semibold text-deep-teal/60 mb-4">
                    Primary Strategies
                  </div>
                  <ul className="space-y-2">
                    {c.strategies.map((s) => (
                      <li
                        key={s}
                        className="text-deep-teal text-sm leading-relaxed flex gap-3"
                      >
                        <span className="text-accent-gold">·</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="lg:col-span-5 bg-deep-teal text-cream p-8 lg:p-12 flex flex-col justify-center">
                <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold">
                  {c.outcomeLabel}
                </div>
                <div className="mt-4 font-serif text-accent-gold text-5xl lg:text-6xl leading-none">
                  {c.outcome}
                </div>
                <p className="mt-6 text-sm text-cream/80 leading-relaxed">
                  Projected outcome after Retirement Tax Strategy Plan
                  implementation. Based on financial modeling at the time of
                  engagement.
                </p>
              </div>
            </article>
          ))}

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 pt-6">
            <p className="text-xs italic text-charcoal-teal/70 leading-relaxed max-w-3xl">
              These examples are illustrative and based on real client
              scenarios with identifying details removed. Projected savings
              are based on financial modeling at the time of engagement and
              depend on actual tax rates, investment returns, and individual
              circumstances. Past results are not a guarantee of future
              outcomes. This content does not constitute tax, legal, or
              investment advice.
            </p>
            <a
              href={FIRM.calendly15}
              target="_blank"
              rel="noreferrer"
              data-testid="results-cta"
              onClick={() =>
                trackEvent("calendly_click_15min", {
                  meta: { source: "results_page" },
                })
              }
              className="group inline-flex items-center gap-3 px-7 py-4 bg-accent-gold !text-white text-xs tracking-overline uppercase font-medium hover:bg-accent-gold-hover transition-all whitespace-nowrap"
            >
              See If Your Situation Qualifies
              <ArrowUpRight
                size={16}
                weight="light"
                className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
              />
            </a>
          </div>
        </div>
      </section>

      <NextStep />
    </Layout>
  );
}
